import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Services from './pages/Services';
import Contact from './pages/Contact';
import Portfolio from './pages/Portfolio';
import Careers from './pages/Careers';
import Haveda from './pages/haveda';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import DashboardStats from './pages/admin/DashboardStats';
import LeadsManagement from './pages/admin/LeadsManagement';
import ProjectsManagement from './pages/admin/ProjectsManagement';
import AccountsManagement from './pages/admin/AccountsManagement';
import Settings from './pages/admin/Settings';
import CRMOverview from './pages/admin/CRMOverview';
import ContactsManagement from './pages/admin/ContactsManagement';
import OpportunitiesManagement from './pages/admin/OpportunitiesManagement';
import ActivitiesManagement from './pages/admin/ActivitiesManagement';
import ProjectDetail from './pages/admin/ProjectDetail';
import QuotesManagement from './pages/admin/sales/QuotesManagement';
import ContractsManagement from './pages/admin/sales/ContractsManagement';
import ExpensesManagement from './pages/admin/expenses/ExpensesManagement';
import ReportingDashboard from './pages/admin/reporting/ReportingDashboard';
import AuditLogViewer from './pages/admin/reporting/AuditLogViewer';
import CareersManagement from './pages/admin/CareersManagement';
import PortfolioManagement from './pages/admin/PortfolioManagement';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { MobileAppProvider } from './utils/MobileAppContext';

import Chatbot from './components/Chatbot';
import ChatbotPage from './pages/ChatbotPage';
import SupportChat from './pages/SupportChat';
import WhatsappWidget from './components/WhatsappWidget';

const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const revealElements = () => {
      const reveals = document.querySelectorAll('.reveal');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      }, { threshold: 0.1 });

      reveals.forEach(el => observer.observe(el));
      return () => observer.disconnect();
    };

    revealElements();
  }, [location]);

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <ScrollToTop />
      <Navbar />
      <main>{children}</main>
      <WhatsappWidget />
      <Chatbot />
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <MobileAppProvider>
      <Router>
        <Routes>
          {/* Admin Routes ... */}
          {/* ... existing routes ... */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardStats />} />
            <Route path="crm" element={<CRMOverview />} />
            <Route path="crm/contacts" element={<ContactsManagement />} />
            <Route path="crm/opportunities" element={<OpportunitiesManagement />} />
            <Route path="crm/activities" element={<ActivitiesManagement />} />
            <Route path="sales/quotes" element={<QuotesManagement />} />
            <Route path="sales/contracts" element={<ContractsManagement />} />
            <Route
              path="users"
              element={
                <ProtectedRoute requiredPage="User Management">
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="leads"
              element={
                <ProtectedRoute requiredPage="Leads Management">
                  <LeadsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects"
              element={
                <ProtectedRoute requiredPage="Project Management">
                  <ProjectsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="projects/:id"
              element={
                <ProtectedRoute requiredPage="Project Management">
                  <ProjectDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="accounts"
              element={
                <ProtectedRoute requiredPage="Account Management">
                  <AccountsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="expenses"
              element={
                <ProtectedRoute requiredPage="Expense Management">
                  <ExpensesManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="careers"
              element={
                <ProtectedRoute requiredPage="Account Management">
                  <CareersManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="portfolio"
              element={
                <ProtectedRoute requiredPage="Portfolio Editor">
                  <PortfolioManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="reporting"
              element={
                <ProtectedRoute requiredPage="Reporting">
                  <ReportingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="audit-logs"
              element={
                <ProtectedRoute requiredPage="Audit Logs">
                  <AuditLogViewer />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute requiredPage="Settings">
                  <Settings />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicLayout>
                <Home />
              </PublicLayout>
            }
          />
          <Route
            path="/services"
            element={
              <PublicLayout>
                <Services />
              </PublicLayout>
            }
          />
          <Route
            path="/portfolio"
            element={
              <PublicLayout>
                <Portfolio />
              </PublicLayout>
            }
          />
          <Route
            path="/haveda"
            element={
              <PublicLayout>
                <Haveda />
              </PublicLayout>
            }
          />
          <Route
            path="/contact"
            element={
              <PublicLayout>
                <Contact />
              </PublicLayout>
            }
          />
          <Route
            path="/careers"
            element={
              <PublicLayout>
                <Careers />
              </PublicLayout>
            }
          />
          {/* Dedicated mobile chatbot screen — no Navbar/Footer */}
          <Route path="/chat" element={<ChatbotPage />} />
          {/* Full-page Support Chat — no Navbar/Footer */}
          <Route path="/support" element={<SupportChat />} />
        </Routes>
      </Router>
    </MobileAppProvider>
  );
};

export default App;

